import { Button } from "@mui/material";
import { Captcha } from "./Captcha";
import PracticeSelector from "./PracticeSelector";
import { useEffect, useState } from "react";
import { useSignUpSender } from "hooks/useSignUpSender";
import { Spinner } from "UI/Spinner";
import * as FP from "utils/fp-js";
import { steps } from "components/NewButtonDialog";
import { ProviderSelector } from "UI/ProviderSelector";
import { AddNewProviderDialog } from "./AddNewProviderDialog";

export const ButtonRequest = ({
  showWarning,
  setError,
  originator,
  handleClose,
  reloadButtons,
  providers,
  addNewProvider,
  step,
  setStep,
  setIsDataChanged,
}) => {
  const [isAddNewProviderDialogOpen, setIsAddNewProviderDialogOpen] =
    useState(false);

  const {
    control,
    setValue,
    currentRecipient,
    setSelectedRecipient,
    handleSubmit,
    onSubmit,
    recipientId,
    isSignUpDone,
    isLoading,
    selectedProviders,
    isProviderSelected,
    handleChangeProvider,
  } = useSignUpSender({
    originator,
    providers,
    showError: setError,
    handleClose,
    setIsDataChanged,
  });

  useEffect(() => {
    if (isSignUpDone) {
      reloadButtons();
      showWarning("New Recipient Button added successfully");
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignUpDone]);

  const addSelectNewlyAddedProvider = (provider) => {
    addNewProvider(provider);
    const providerId = provider?.id;
    if (!providerId) return;
    const providerLabel = [provider?.name, provider?.speciality].join(" - ");
    handleChangeProvider(undefined, providerId, providerLabel);
  };

  return (
    <div className="flex-1 flex flex-col justify-center items-center gap-2 max-w-sm">
      {FP.match(step)
        .on(
          steps.SELECT_PROVIDER,
          <>
            <div className="text-black mt-4 text-center text-sm sm:text-lg">
              Select the Provider from the list:
            </div>
            <form
              className="flex flex-col sm:flex-row rounded-xl gap-6"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="flex flex-col justify-center items-stretch gap-1 w-full">
                <div className="max-h-72 overflow-y-auto">
                  <ProviderSelector
                    providers={selectedProviders}
                    handleChangeProvider={handleChangeProvider}
                    isLoading={isLoading}
                    noHeader
                  />
                </div>
                <div className="mt-2 mb-6 text-xs sm:text-sm flex flex-row justify-center items-center flex-wrap gap-2">
                  <Button onClick={() => setIsAddNewProviderDialogOpen(true)}>
                    Create New Provider
                  </Button>
                  <AddNewProviderDialog
                    open={isAddNewProviderDialogOpen}
                    handleClose={() => setIsAddNewProviderDialogOpen(false)}
                    organizationId={originator?.organizationId}
                    setResultValue={addSelectNewlyAddedProvider}
                    setError={setError}
                  />
                </div>
                <div className="flex flex-col justify-center items-stretch gap-5 w-full">
                  <Button
                    type="button"
                    variant="contained"
                    sx={{ padding: "8px 32px" }}
                    disabled={isLoading || !isProviderSelected}
                    onClick={() => setStep(steps.SELECT_RECIPIENT)}
                  >
                    Next Step
                  </Button>
                </div>
              </div>
            </form>
          </>
        )
        .on(
          steps.SELECT_RECIPIENT,
          <>
            <div className="text-black mt-4 text-center text-sm sm:text-lg">
              Where would you like to send referrals?
            </div>
            <form
              className="flex flex-col sm:flex-row rounded-xl gap-6"
              onSubmit={handleSubmit(onSubmit)}
            >
              <div className="flex flex-col justify-center items-stretch gap-1 w-full">
                <PracticeSelector
                  id={"id"}
                  setValue={setSelectedRecipient}
                  value={currentRecipient}
                  publicMode
                  recipientsOnly
                />
                <div className="mt-2 mb-6 text-xs sm:text-sm flex flex-row justify-center items-center flex-wrap gap-2">
                  <div className="text-gray-500">
                    Can’t find your Recipient? Call or text us:
                  </div>
                  <a href="tel:346-512-0751">
                    <span className="text-blue-700">(346) 512-0751</span>
                  </a>
                </div>
                <div className="flex flex-col justify-center items-stretch gap-5 w-full">
                  <Captcha
                    control={control}
                    setValue={setValue}
                    setError={setError}
                  />
                  <div className="flex flex-row gap-4 justify-end">
                    <Button
                      type="button"
                      variant="outlined"
                      sx={{ padding: "8px 32px" }}
                      disabled={isLoading}
                      onClick={() => setStep(steps.SELECT_PROVIDER)}
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      sx={{ padding: "8px 32px" }}
                      disabled={!recipientId || isLoading}
                    >
                      Add New Button
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          </>
        )
        .otherwise(null)}{" "}
      {isLoading && <Spinner />}
    </div>
  );
};
